import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './side-sheet.css';

const EXIT_MS = 220;

/**
 * A floating detail sheet that slides in from the right edge, over the page.
 *
 * NON-modal on purpose: no backdrop and no focus trap, so the list underneath
 * stays clickable and a click on another row simply swaps the sheet's
 * content. Esc closes it. Bootstrap modals opened from inside the sheet stack
 * above it (z-index 1040 < 1050).
 *
 * Not react-bootstrap's Offcanvas — the bundled theme CSS predates Bootstrap
 * 5's offcanvas module, so it renders unstyled (see AssetDetail.jsx).
 *
 * The last open children are kept through the exit animation, so callers can
 * render `{selected && <Detail />}` style content without the sheet blanking
 * on its way out.
 */
export default function SideSheet({ open, onClose, label, width = 560, children }) {
    const [mounted, setMounted] = useState(open);
    const lastChildren = useRef(children);
    if (open) lastChildren.current = children;

    useEffect(() => {
        if (open) {
            setMounted(true);
            return undefined;
        }
        const timer = setTimeout(() => setMounted(false), EXIT_MS);
        return () => clearTimeout(timer);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            // A modal on top owns Esc.
            if (document.body.classList.contains('modal-open')) return;
            onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    if (!open && !mounted) return null;

    return ReactDOM.createPortal(
        <aside
            className={`ll-side-sheet${open ? '' : ' ll-side-sheet--closing'}`}
            style={{ '--ll-side-sheet-width': `${width}px` }}
            role="dialog"
            aria-modal="false"
            aria-label={label}
        >
            {lastChildren.current}
        </aside>,
        document.body,
    );
}
